import React from 'react';

import { Button, DatePicker } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { CalendarViewMode } from '../../calendar/planned-delivery-windows-calendar';

import styles from './week-date-range-picker.module.scss';

interface Props {
	changeCalendarRangeDates?: (startTime: string, endTime: string) => void;
	calendarRangeDates?: { startDate: string; endDate: string };
	timezone: string;
	calendarViewMode?: CalendarViewMode;
}

const WeekDateRangePicker = ({ calendarRangeDates, changeCalendarRangeDates, timezone, calendarViewMode }: Props) => {
	const { t } = useTranslation();

	const getDayText = () => {
		const fullDateFormat = 'DD MMM YYYY';
		return (
			<span className={styles.weekRangeDates}>{`${moment(calendarRangeDates.startDate)
				.tz(timezone)
				.format(fullDateFormat)}`}</span>
		);
	};

	const getWeekText = () => {
		const startRange = moment(calendarRangeDates.startDate).tz(timezone);
		const endRange = moment(calendarRangeDates.endDate).tz(timezone);
		const isSameMonth = startRange.month() === endRange.month();
		const isSameYear = startRange.year() === endRange.year();
		const fullDateFormat = 'DD MMM YYYY';
		const dateAndMonthFormat = 'DD MMM';
		const startDateFormat = isSameMonth && isSameYear ? 'DD' : isSameYear ? dateAndMonthFormat : fullDateFormat;
		const endDateFormat = fullDateFormat;
		return (
			<>
				<span className={styles.weekNumber}>{`${t('PLANNED_DELIVERY_WINDOWS_TOOLBAR.WEEK_TEXT')} ${moment(
					calendarRangeDates.startDate
				)
					.tz(timezone)
					.format('ww')} `}</span>
				<span className={styles.weekRangeDates}>{`(${moment(calendarRangeDates.startDate)
					.tz(timezone)
					.format(startDateFormat)} - ${moment(calendarRangeDates.endDate)
					.tz(timezone)
					.format(endDateFormat)})`}</span>
			</>
		);
	};

	const goToNext = () => {
		changeCalendarRangeDates(
			moment(calendarRangeDates.startDate).tz(timezone).add(1, calendarViewMode).toISOString(),
			moment(calendarRangeDates.endDate).tz(timezone).add(1, calendarViewMode).toISOString()
		);
	};

	const goToPrev = () => {
		changeCalendarRangeDates(
			moment(calendarRangeDates.startDate).tz(timezone).subtract(1, calendarViewMode).toISOString(),
			moment(calendarRangeDates.endDate).tz(timezone).subtract(1, calendarViewMode).toISOString()
		);
	};

	return (
		<div className={styles.weekPickerWrapper}>
			<div className={styles.weekPicker}>
				<div className={styles.weekPickerText}>
					{calendarViewMode === CalendarViewMode.week ? getWeekText() : getDayText()}
				</div>
				<DatePicker
					value={moment(calendarRangeDates.startDate).tz(timezone)}
					className={styles.weekDatePicker}
					picker={calendarViewMode === CalendarViewMode.week ? calendarViewMode : 'date'}
					onChange={(date: Moment) =>
						changeCalendarRangeDates(
							date.tz(timezone).startOf(calendarViewMode).toISOString(),
							date.tz(timezone).endOf(calendarViewMode).toISOString()
						)
					}
					data-test-id="planned-delivery-windows-toolbar-range-picker"
					format=" "
					allowClear={false}
				/>
			</div>
			<Button
				icon={<BringgIcon iconName={BringgFontIcons.ChevronLeft} />}
				data-test-id="prev-week-button"
				onClick={goToPrev}
				shape="circle"
				type="text"
			/>
			<Button
				icon={<BringgIcon iconName={BringgFontIcons.ChevronRight} />}
				onClick={goToNext}
				shape="circle"
				type="text"
				data-test-id="next-week-button"
			/>
		</div>
	);
};

export default WeekDateRangePicker;
